// Design System DatePicker Component
// Unified date picker component

import React, { forwardRef } from 'react';
import { DatePicker as MuiDatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import TextField from './TextField';
import PropTypes from 'prop-types';

/**
 * DatePicker - Unified date picker component
 * 
 * Features:
 * - Uses TextField for consistent styling
 * - Supports all date formats
 * - Min/max date validation
 * - Disabled dates
 * - Mobile-friendly
 * 
 * @example
 * // Basic usage
 * <DatePicker
 *   label="Birth Date"
 *   value={birthDate}
 *   onChange={setBirthDate}
 * />
 * 
 * // With restrictions
 * <DatePicker
 *   label="Appointment Date"
 *   value={date}
 *   onChange={setDate}
 *   minDate={new Date()}
 *   maxDate={addMonths(new Date(), 3)}
 * />
 */
const DatePicker = forwardRef(({
  label,
  error,
  helperText,
  fullWidth = true,
  format = 'MM/dd/yyyy',
  ...props
}, ref) => {
  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <MuiDatePicker
        ref={ref}
        label={label}
        format={format}
        slotProps={{
          textField: {
            fullWidth,
            error: Boolean(error),
            helperText,
            variant: 'outlined',
          },
        }}
        slots={{
          textField: TextField,
        }}
        {...props}
      />
    </LocalizationProvider>
  );
});

DatePicker.propTypes = {
  label: PropTypes.string,
  error: PropTypes.bool,
  helperText: PropTypes.node,
  fullWidth: PropTypes.bool,
  format: PropTypes.string,
  value: PropTypes.any,
  onChange: PropTypes.func,
  minDate: PropTypes.any,
  maxDate: PropTypes.any,
  // All other MUI DatePicker props are supported
};

DatePicker.displayName = 'DatePicker';

export default DatePicker;